/**
 * The stage: renderer, scene, the four lights we are allowed (ART-BIBLE §6), the clock.
 * Deliberately small — the look lives in the materials, not in the renderer settings.
 */

import {
  WebGLRenderer, Scene, PerspectiveCamera, HemisphereLight, DirectionalLight, PointLight,
  Fog, Color, NoToneMapping, SRGBColorSpace, PCFSoftShadowMap, Vector2, Vector3, Clock, MathUtils,
} from 'three';

import { NACHT, RIM, NEON } from '../art/palette';
import { uTime, uPxScale, uKeyDir } from '../art/toon';
import { updateNeon } from '../art/neon';
import { setMaxAnisotropy } from '../art/textures';

const _keyWorld = new Vector3();

export type Quality = 'low' | 'high';

export interface StageOpts {
  quality?: Quality;
  /** fixed pixel size — the screenshot gates pin this so shots are comparable */
  size?: [number, number];
  shadows?: boolean;
}

export interface Stage {
  renderer: WebGLRenderer;
  scene: Scene;
  camera: PerspectiveCamera;
  key: DirectionalLight;
  ambient: HemisphereLight;
  neonLights: [PointLight, PointLight];
  clock: Clock;
  time: number;
  /** simulation timescale — the Bank-sprengen slow-mo pulls this to 0.25 */
  timescale: number;
  resize(w?: number, h?: number): void;
  beginFrame(dt: number): void;
  render(): void;
  stats(): { drawCalls: number; triangles: number; programs: number };
  dispose(): void;
}

export function createStage(el: HTMLElement, opts: StageOpts = {}): Stage {
  const quality = opts.quality ?? 'high';
  const canvas = document.createElement('canvas');
  canvas.style.display = 'block';
  canvas.style.width = '100%';
  canvas.style.height = '100%';
  el.appendChild(canvas);

  const renderer = new WebGLRenderer({
    canvas,
    antialias: true,
    alpha: false,
    powerPreference: 'high-performance',
    stencil: false,
  });
  renderer.setPixelRatio(quality === 'low' ? 1 : Math.min(2, window.devicePixelRatio || 1));
  renderer.outputColorSpace = SRGBColorSpace;
  renderer.toneMapping = NoToneMapping;          // flat cel colours; §6
  renderer.setClearColor(new Color(NACHT.void), 1);
  renderer.shadowMap.enabled = opts.shadows !== false;
  renderer.shadowMap.type = PCFSoftShadowMap;

  // Printed matter (marquees, the Hausregeln board, the decal sheet) is minified hard in
  // the aisle. Ask the driver what it can do rather than guessing a number: this is the
  // difference between MÜNZSCHIEBER and "HUNTSCHIEBLU" three cabinets away. §5, §9.
  setMaxAnisotropy(renderer.capabilities.getMaxAnisotropy());

  const scene = new Scene();
  scene.background = new Color(NACHT.void);
  // fog DARKENS toward the room's own night colour — it never greys the palette
  scene.fog = new Fog(NACHT.deep, 120, 340);

  const camera = new PerspectiveCamera(38, 16 / 9, 1, 600);
  camera.position.set(0, 19, 40);

  // --- the four lights ------------------------------------------------------
  // 1. ambient bounce: cold sky, warm floor kick
  const ambient = new HemisphereLight(new Color(0x6d7cb4), new Color(0x3d2e20), 0.42);
  scene.add(ambient);

  // 2. the window: moonlight, cold, from up-camera-left. This is the rim's sky.
  const key = new DirectionalLight(new Color(RIM), 1.15);
  key.position.set(-40, 62, 44);
  key.castShadow = opts.shadows !== false;
  const sm = quality === 'low' ? 512 : 1024;
  key.shadow.mapSize = new Vector2(sm, sm);
  key.shadow.camera.near = 5;
  key.shadow.camera.far = 220;
  key.shadow.camera.left = -70; key.shadow.camera.right = 70;
  key.shadow.camera.top = 70; key.shadow.camera.bottom = -50;
  key.shadow.bias = -0.0016;
  key.shadow.normalBias = 0.6;
  key.shadow.radius = 2.5;
  // cast shadows must land in the ramp's shadow BAND, not in a black hole
  key.shadow.intensity = 0.72;
  scene.add(key);
  scene.add(key.target);

  // 3+4. the two nearest neons, faked as point lights
  const p1 = new PointLight(new Color(NEON.magenta.core), 0, 60, 2.0);
  const p2 = new PointLight(new Color(NEON.cyan.core), 0, 60, 2.0);
  scene.add(p1, p2);

  const clock = new Clock();

  const stage: Stage = {
    renderer, scene, camera, key, ambient, neonLights: [p1, p2], clock,
    time: 0, timescale: 1,

    resize(w?: number, h?: number) {
      const W = w ?? opts.size?.[0] ?? el.clientWidth ?? 960;
      const H = h ?? opts.size?.[1] ?? el.clientHeight ?? 540;
      renderer.setSize(W, H, false);
      camera.aspect = W / Math.max(1, H);
      camera.updateProjectionMatrix();
      // screen-space constant outline width & halftone depend on this
      const px = renderer.getPixelRatio();
      uPxScale.value = (2 * Math.tan(MathUtils.degToRad(camera.fov) / 2)) / (H * px);
    },

    beginFrame(dt: number) {
      stage.time += dt;
      uTime.value = stage.time;
      updateNeon(stage.time);
      uPxScale.value = (2 * Math.tan(MathUtils.degToRad(camera.fov) / 2)) / (renderer.domElement.height || 540);
      // the halftone needs the true terminator, so it needs the key light in view space
      camera.updateMatrixWorld();
      _keyWorld.copy(key.position).sub(key.target.position).normalize();
      uKeyDir.value.copy(_keyWorld).transformDirection(camera.matrixWorldInverse).normalize();
    },

    render() {
      renderer.render(scene, camera);
    },

    stats() {
      const i = renderer.info;
      return { drawCalls: i.render.calls, triangles: i.render.triangles, programs: i.programs?.length ?? 0 };
    },

    dispose() {
      renderer.dispose();
      canvas.remove();
    },
  };

  stage.resize();
  return stage;
}
